import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import PhotoUpload from '../components/PhotoUpload';
import LocationPicker from '../components/LocationPicker';
import { ArrowLeft, Save, Loader2, Briefcase, MapPin, Building } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCrearTrabajo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [tecnicos, setTecnicos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [lugares, setLugares] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [selectedClienteId, setSelectedClienteId] = useState('none');
  
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    tecnico_asignado_id: 'none',
    cliente_id: 'none',
    lugar_id: 'none',
    ciudad_id: 'none',
    direccion: '',
    estado: 'pendiente',
    materiales_usados: '',
    observaciones: '',
    horas_trabajadas: '',
    fecha_trabajo: new Date().toISOString().split('T')[0] // Fecha actual por defecto
  });
  const [fotos, setFotos] = useState([]);
  const [ubicacion, setUbicacion] = useState(null);

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      setLoadingData(true);
      const token = localStorage.getItem('token');
      const [tecnicosRes, clientesRes, ciudadesRes] = await Promise.all([
        axios.get(`${API}/admin/tecnicos`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/clientes-empresa`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/ciudades`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTecnicos(tecnicosRes.data);
      setClientes(clientesRes.data);
      setCiudades(ciudadesRes.data);
    } catch (err) {
      setError('Error al cargar datos');
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  };

  const loadLugaresPorCliente = async (clienteId) => {
    if (!clienteId || clienteId === 'none') {
      setLugares([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/lugares?cliente_id=${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLugares(response.data);
    } catch (err) {
      console.error('Error al cargar lugares:', err);
      setLugares([]);
    }
  };

  const handleClienteChange = (clienteId) => {
    setSelectedClienteId(clienteId);
    setFormData({ ...formData, cliente_id: clienteId, lugar_id: 'none' });
    loadLugaresPorCliente(clienteId);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const dataToSend = {
        titulo: formData.titulo,
        descripcion: formData.descripcion,
        direccion: formData.direccion,
        estado: formData.estado,
        materiales_usados: formData.materiales_usados,
        observaciones: formData.observaciones,
        horas_trabajadas: formData.horas_trabajadas ? parseFloat(formData.horas_trabajadas) : null,
        fotos,
        ubicacion_gps: ubicacion
      };

      console.log('Datos a enviar:', dataToSend);

      // Agregar técnico si fue seleccionado
      if (formData.tecnico_asignado_id && formData.tecnico_asignado_id !== 'none') {
        const tecnico = tecnicos.find(t => t.id === formData.tecnico_asignado_id);
        if (tecnico) {
          dataToSend.tecnico_asignado_id = tecnico.id;
          dataToSend.tecnico_asignado_nombre = tecnico.nombre_completo;
          console.log('Técnico asignado:', tecnico.nombre_completo);
        }
      }

      // Agregar cliente si fue seleccionado
      if (formData.cliente_id && formData.cliente_id !== 'none') {
        const cliente = clientes.find(c => c.id === formData.cliente_id);
        if (cliente) {
          dataToSend.cliente_id = cliente.id;
          dataToSend.cliente_nombre = cliente.nombre;
          console.log('Cliente asignado:', cliente.nombre);
        }
      }

      // Agregar lugar si fue seleccionado
      if (formData.lugar_id && formData.lugar_id !== 'none') {
        const lugar = lugares.find(l => l.id === formData.lugar_id);
        if (lugar) {
          dataToSend.lugar_id = lugar.id;
          dataToSend.lugar_nombre = lugar.nombre;
          console.log('Lugar asignado:', lugar.nombre);
        }
      }

      // Agregar ciudad si fue seleccionada
      if (formData.ciudad_id && formData.ciudad_id !== 'none') {
        const ciudad = ciudades.find(c => c.id === formData.ciudad_id);
        if (ciudad) {
          dataToSend.ciudad_id = ciudad.id;
          dataToSend.ciudad_nombre = ciudad.nombre;
          console.log('Ciudad asignada:', ciudad.nombre);
        }
      }

      console.log('Datos finales a enviar:', dataToSend);
      console.log('Enviando a:', `${API}/trabajos`);

      await axios.post(`${API}/trabajos`, dataToSend);
      navigate('/admin/trabajos');
    } catch (err) {
      console.error('Error completo:', err);
      console.error('Error response:', err.response);
      
      let errorMsg = 'Error al crear el trabajo';
      if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6" data-testid="admin-crear-trabajo">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Panel
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/crear-tecnico')}
            >
              Crear Técnico
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (selectedClienteId && selectedClienteId !== 'none') {
                  navigate(`/admin/clientes-empresa/${selectedClienteId}/lugares`);
                } else {
                  alert('Primero selecciona un cliente en el formulario');
                }
              }}
              disabled={!selectedClienteId || selectedClienteId === 'none'}
            >
              <MapPin className="mr-1 h-4 w-4" />
              Crear Lugar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/ciudades')}
            >
              <Building className="mr-1 h-4 w-4" />
              Crear Ciudad
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Briefcase className="h-6 w-6" />
              Crear Nuevo Trabajo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" data-testid="form-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Título */}
              <div className="space-y-2">
                <Label htmlFor="titulo">Título del Trabajo *</Label>
                <Input
                  id="titulo"
                  name="titulo"
                  placeholder="Ej: Instalación de sistema eléctrico"
                  value={formData.titulo}
                  onChange={handleChange}
                  required
                  data-testid="titulo-input"
                />
              </div>

              {/* Descripción */}
              <div className="space-y-2">
                <Label htmlFor="descripcion">Descripción *</Label>
                <Textarea
                  id="descripcion"
                  name="descripcion"
                  placeholder="Describe el trabajo a realizar..."
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="descripcion-input"
                />
              </div>

              {/* Fecha del Trabajo */}
              <div className="space-y-2">
                <Label htmlFor="fecha_trabajo">Fecha del Trabajo *</Label>
                <Input
                  id="fecha_trabajo"
                  name="fecha_trabajo"
                  type="date"
                  value={formData.fecha_trabajo}
                  onChange={handleChange}
                  required
                  data-testid="fecha-trabajo-input"
                />
                <p className="text-xs text-gray-500">
                  Fecha en que se realizará o se realizó el trabajo
                </p>
              </div>

              {/* Técnico y Cliente - DROPDOWNS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tecnico">Asignar Técnico</Label>
                  <Select 
                    value={formData.tecnico_asignado_id} 
                    onValueChange={(value) => setFormData({ ...formData, tecnico_asignado_id: value })}
                  >
                    <SelectTrigger data-testid="tecnico-select">
                      <SelectValue placeholder="Selecciona un técnico" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {tecnicos.map((tecnico) => (
                        <SelectItem key={tecnico.id} value={tecnico.id}>
                          {tecnico.nombre_completo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Puedes asignarlo después en "Gestionar Trabajos"</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cliente">Asignar Cliente</Label>
                  <Select 
                    value={formData.cliente_id} 
                    onValueChange={handleClienteChange}
                  >
                    <SelectTrigger data-testid="cliente-select">
                      <SelectValue placeholder="Selecciona un cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {clientes.map((cliente) => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Lugar y Ciudad */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lugar">Lugar</Label>
                  <Select 
                    value={formData.lugar_id} 
                    onValueChange={(value) => setFormData({ ...formData, lugar_id: value })}
                    disabled={!selectedClienteId || selectedClienteId === 'none'}
                  >
                    <SelectTrigger data-testid="lugar-select">
                      <SelectValue placeholder={selectedClienteId === 'none' ? "Primero selecciona un cliente" : "Selecciona un lugar"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {lugares.map((lugar) => (
                        <SelectItem key={lugar.id} value={lugar.id}>
                          {lugar.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ciudad">Ciudad</Label>
                  <Select 
                    value={formData.ciudad_id} 
                    onValueChange={(value) => setFormData({ ...formData, ciudad_id: value })}
                  >
                    <SelectTrigger data-testid="ciudad-select">
                      <SelectValue placeholder="Selecciona una ciudad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {ciudades.map((ciudad) => (
                        <SelectItem key={ciudad.id} value={ciudad.id}>
                          {ciudad.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dirección */}
              <div className="space-y-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  name="direccion"
                  placeholder="Dirección del trabajo"
                  value={formData.direccion}
                  onChange={handleChange}
                  data-testid="direccion-input"
                />
              </div>

              {/* Estado y Horas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="estado">Estado</Label>
                  <Select value={formData.estado} onValueChange={(value) => setFormData({ ...formData, estado: value })}>
                    <SelectTrigger data-testid="estado-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="en_progreso">En Progreso</SelectItem>
                      <SelectItem value="completado">Completado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="horas_trabajadas">Horas Estimadas</Label>
                  <Input
                    id="horas_trabajadas"
                    name="horas_trabajadas"
                    type="number"
                    step="0.5"
                    placeholder="Ej: 4.5"
                    value={formData.horas_trabajadas}
                    onChange={handleChange}
                    data-testid="horas-input"
                  />
                </div>
              </div>

              {/* Materiales */}
              <div className="space-y-2">
                <Label htmlFor="materiales_usados">Materiales Necesarios</Label>
                <Textarea
                  id="materiales_usados"
                  name="materiales_usados"
                  placeholder="Lista de materiales..."
                  value={formData.materiales_usados}
                  onChange={handleChange}
                  rows={3}
                  data-testid="materiales-input"
                />
              </div>

              {/* Observaciones */}
              <div className="space-y-2">
                <Label htmlFor="observaciones">Observaciones</Label>
                <Textarea
                  id="observaciones"
                  name="observaciones"
                  placeholder="Notas adicionales..."
                  value={formData.observaciones}
                  onChange={handleChange}
                  rows={3}
                  data-testid="observaciones-input"
                />
              </div>

              {/* Ubicación GPS */}
              <div className="space-y-2">
                <Label>Ubicación GPS</Label>
                <LocationPicker location={ubicacion} onLocationChange={setUbicacion} />
              </div>

              {/* Fotos */}
              <div className="space-y-2">
                <Label>Fotografías</Label>
                <PhotoUpload photos={fotos} onPhotosChange={setFotos} />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1"
                  data-testid="save-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creando...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Crear Trabajo
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCrearTrabajo;
